import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/* Fee data                                                            */
/* ------------------------------------------------------------------ */

const REGIONAL_FEES = [
  { region: "London", sole: "1.0-1.5%", multi: "2.5-3.0%", avg: "1.36%" },
  { region: "South East", sole: "1.0-1.5%", multi: "2.5-3.0%", avg: "1.28%" },
  { region: "South West", sole: "1.0-1.5%", multi: "2.5-3.0%", avg: "1.20%" },
  { region: "East of England", sole: "1.0-1.5%", multi: "2.5-3.0%", avg: "1.22%" },
  { region: "West Midlands", sole: "1.0-1.5%", multi: "2.0-2.5%", avg: "1.15%" },
  { region: "East Midlands", sole: "1.0-1.5%", multi: "2.0-2.5%", avg: "1.10%" },
  { region: "North West", sole: "0.75-1.25%", multi: "2.0-2.5%", avg: "1.05%" },
  { region: "North East", sole: "0.75-1.25%", multi: "2.0-2.5%", avg: "1.00%" },
  { region: "Yorkshire", sole: "0.75-1.25%", multi: "2.0-2.5%", avg: "1.05%" },
  { region: "Wales", sole: "1.0-1.5%", multi: "2.0-2.5%", avg: "1.10%" },
]

const FAQ = [
  {
    q: "Are estate agent fees negotiable?",
    a: "Yes. Estate agent fees are always negotiable. Most agents will consider reducing their fee, especially for higher-value properties or if you agree to a sole agency contract. However, the cheapest agent is rarely the best choice. Focus on the agent's marketing quality, track record, and sale-price performance.",
  },
  {
    q: "What is the difference between sole agency and multi-agency?",
    a: "With sole agency, you instruct one agent to sell your property. Fees are lower (typically 1.0-1.5% + VAT) because the agent is guaranteed the commission if a buyer is found. With multi-agency, you instruct two or more agents. Fees are higher (2.5-3.0% + VAT) but only the agent who finds the buyer gets paid.",
  },
  {
    q: "Do I pay if my house doesn't sell?",
    a: "In most cases, no. Estate agent fees are typically payable on completion only, meaning you pay when the sale goes through. Some contracts include a minimum term or tie-in period. Always read the contract carefully before signing and check for any withdrawal fees.",
  },
  {
    q: "How much does an online estate agent cost?",
    a: "Online agents typically charge a flat fee of £999-1,499 regardless of sale price. This can be cheaper for higher-value properties but usually comes with less personal service, fewer viewings conducted by the agent, and limited local market knowledge. The fee is often payable upfront whether the property sells or not.",
  },
  {
    q: "What should an estate agent fee include?",
    a: "A good agent's fee should include: a market appraisal, professional photography, floorplans, property particulars, portal listings (Rightmove, Zoopla), a for-sale board, accompanied viewings, buyer qualification, offer negotiation, and sale progression through to completion. Premium agents also offer video marketing and social media campaigns.",
  },
]

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function EstateAgentFeesGuide({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Estate Agent Fees UK 2026: Complete Guide",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-22",
            dateModified: "2026-02-22",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Guides", path: "/guides" }, { name: "Estate Agent Fees" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Estate agent fees UK
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            How much do estate agents charge? A complete guide to UK estate
            agent fees in 2026, including average rates by region, sole vs
            multi-agency, and tips for getting the best value.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Content */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          {/* Section 1: Overview */}
          <h2 className="font-display text-2xl font-bold text-charcoal">
            How much do estate agents charge<span className="text-red">?</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              The average estate agent fee in the UK is approximately{" "}
              <strong className="text-charcoal">1.18% + VAT</strong> for a
              sole agency agreement. That means on a £350,000 property,
              you&rsquo;d pay around £4,130 + VAT (£4,956 including VAT).
            </p>
            <p>
              Fees vary significantly by region, property value, and the level
              of service provided. London agents tend to charge more than
              those in the north, and agents who invest in premium marketing
              (professional video, social media campaigns) may charge a
              slightly higher percentage, but often achieve better sale prices.
            </p>
            <p>
              All fees quoted below are exclusive of VAT unless stated
              otherwise. VAT-registered agents add 20% on top.
            </p>
          </div>

          {/* Section 2: Regional fees table */}
          <h2 className="mt-12 font-display text-2xl font-bold text-charcoal">
            Average fees by region<span className="text-red">.</span>
          </h2>
          <p className="mt-2 text-sm text-slate">
            Typical fee ranges for sole agency and multi-agency agreements
            across the UK (exc. VAT).
          </p>

          <div className="mt-6 overflow-x-auto rounded-xl border border-stone">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone bg-cream/50">
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    Region
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-charcoal">
                    Sole agency
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-charcoal">
                    Multi-agency
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-charcoal">
                    Average
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone">
                {REGIONAL_FEES.map((row) => (
                  <tr key={row.region} className="hover:bg-cream/30">
                    <td className="px-4 py-3 font-medium text-charcoal">
                      {row.region}
                    </td>
                    <td className="px-4 py-3 text-right text-slate">
                      {row.sole}
                    </td>
                    <td className="px-4 py-3 text-right text-slate">
                      {row.multi}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-charcoal">
                      {row.avg}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-warm-grey">
            Figures are indicative and based on industry averages. Actual fees
            will vary by individual agent and property.
          </p>

          {/* Section 3: Types of agreement */}
          <h2 className="mt-12 font-display text-2xl font-bold text-charcoal">
            Types of agency agreement<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-6">
            <div className="rounded-xl border border-stone bg-white p-5">
              <h3 className="font-display text-lg font-bold text-charcoal">
                Sole agency
              </h3>
              <p className="mt-2 text-sm text-slate">
                You instruct one agent exclusively. This is the most common
                arrangement and attracts the lowest fees (typically 1.0-1.5%
                + VAT). The agent is motivated to sell quickly because they
                know they&rsquo;ll earn the commission. Most sole agency
                contracts run for 8-16 weeks.
              </p>
            </div>

            <div className="rounded-xl border border-stone bg-white p-5">
              <h3 className="font-display text-lg font-bold text-charcoal">
                Multi-agency
              </h3>
              <p className="mt-2 text-sm text-slate">
                You instruct two or more agents simultaneously. Only the
                agent who finds the buyer gets paid, but fees are higher
                (typically 2.5-3.0% + VAT) to compensate for the risk of
                doing work without getting paid. Best suited for properties
                that have struggled to sell with one agent.
              </p>
            </div>

            <div className="rounded-xl border border-stone bg-white p-5">
              <h3 className="font-display text-lg font-bold text-charcoal">
                Joint sole agency
              </h3>
              <p className="mt-2 text-sm text-slate">
                Two agents work together to sell your property and share the
                commission between them. Fees sit between sole and
                multi-agency rates (typically 1.5-2.0% + VAT). This can be
                useful when a property needs exposure across different areas
                or market segments.
              </p>
            </div>
          </div>

          {/* Section 4: Online vs high street */}
          <h2 className="mt-12 font-display text-2xl font-bold text-charcoal">
            Online agents vs high street<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Online agents (like Purplebricks and Strike) typically charge a
              flat fee of £999-1,499, payable upfront or on completion. This
              can represent significant savings on higher-value properties.
            </p>
            <p>
              However, high street agents offer local market expertise,
              accompanied viewings, and personal service that online agents
              often lack. Studies consistently show that properties sold
              through local agents achieve higher sale prices on average,
              often more than offsetting the higher fee.
            </p>
            <p>
              The best high street agents combine local knowledge with modern
              marketing techniques: professional photography, video tours,
              and social media campaigns. This combination of personal
              service and digital marketing consistently outperforms
              either approach alone.
            </p>
          </div>

          {/* Section 5: Negotiating */}
          <h2 className="mt-12 font-display text-2xl font-bold text-charcoal">
            Negotiating estate agent fees<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Most agents expect some negotiation. Here are practical tips
              for getting a fair deal without compromising on service:
            </p>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-slate">
            <li className="flex gap-2">
              <span className="mt-0.5 text-red">&#8226;</span>
              <span>
                <strong className="text-charcoal">Get three valuations.</strong>{" "}
                Compare fees, marketing packages, and sale-price estimates
                before deciding.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-0.5 text-red">&#8226;</span>
              <span>
                <strong className="text-charcoal">Ask what&rsquo;s included.</strong>{" "}
                A lower fee that excludes professional photography or
                floorplans may cost more in the long run.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-0.5 text-red">&#8226;</span>
              <span>
                <strong className="text-charcoal">Focus on net proceeds.</strong>{" "}
                An agent charging 1.5% who achieves £360,000 nets you more
                than one charging 0.75% who achieves £340,000.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-0.5 text-red">&#8226;</span>
              <span>
                <strong className="text-charcoal">Check the contract.</strong>{" "}
                Look for tie-in periods, withdrawal fees, and notice periods.
                A 12-week sole agency with 2 weeks&rsquo; notice is standard.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-0.5 text-red">&#8226;</span>
              <span>
                <strong className="text-charcoal">Look for video marketing.</strong>{" "}
                Properties with video get 2x more enquiries. Agents who
                include video in their fee deliver better results.
              </span>
            </li>
          </ul>

          {/* Section 6: What to look for */}
          <h2 className="mt-12 font-display text-2xl font-bold text-charcoal">
            What a good agent&rsquo;s fee should include<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              At a minimum, expect your agent&rsquo;s fee to cover the
              following:
            </p>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              "Market appraisal and pricing strategy",
              "Professional photography",
              "Floorplan drawings",
              "Property particulars and brochure",
              "Rightmove and Zoopla listings",
              "For-sale board",
              "Accompanied viewings",
              "Buyer qualification and vetting",
              "Offer negotiation",
              "Sale progression to completion",
            ].map((item) => (
              <div
                key={item}
                className="flex items-start gap-2 rounded-lg border border-stone bg-white px-4 py-3"
              >
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="mt-0.5 size-4 shrink-0 text-red"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm text-charcoal">{item}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm text-slate">
            Premium agents also include video marketing, social media
            campaigns, and targeted advertising. These extras typically drive
            more enquiries and faster sales, more than justifying any fee
            premium.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "1.18%", label: "average UK estate agent fee (exc. VAT)" },
            { stat: "73%", label: "of vendors prefer agents offering video" },
            { stat: "2x", label: "more enquiries on listings with video" },
            { stat: "40%", label: "of vendors say marketing influenced their choice" },
          ].map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related resources<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              { href: "/tools/commission-calculator", title: "Commission calculator", desc: "Calculate exact fees and see what a small increase is worth." },
              { href: "/directory", title: "Agent directory", desc: "Compare local estate agents in your area." },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg viewBox="0 0 20 20" fill="currentColor" className="size-3.5 transition-transform group-hover:translate-x-0.5">
                    <path fillRule="evenodd" d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z" clipRule="evenodd" />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            The best agents prove their worth
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Outstanding marketing justifies premium fees. See how QuiteRight
            helps agents deliver scroll-stopping video ads that win
            instructions and sell properties faster.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/examples"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              See video examples
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
